import { Button } from '@mui/material';
import React, { useState } from 'react';
import { Row, Col } from 'antd';
import { useNotify, useRefresh } from 'react-admin';

import { rbac, showAxiosError } from '@utils';
import { Credit, CreditStatus } from '@types';
import { CreditCard } from './CreditCard';
import { CreditOrders } from './CreditOrders';
import { CreditContracts } from './CreditContracts';
import { ExportRisk } from '@pages/credit-risk/export';
import { CreditTimeTracker } from './CreditTimeTracker';
import { CreditCallsWidget } from '@pages/credit/CreditEdit/CreditCallsWidget';
import { InitialCard, InitialPage, LightTooltip, ScoreWidget } from '@components';
import { ExportCreditTerms } from '@pages/credit-risk/export/ExportCreditTerms';
import { CreditInvoiceWidget } from '@pages/credit/CreditInitial/CreditInvoiceWidget';
import { CreditContractsDocuments } from '@pages/credit/CreditInitial/CreditContractsDocuments';
import { creditHttp } from '@network';
import { CreditChatBtn } from '@pages/credit/CreditChatBtn';
import cls from './credit-initial.module.css';


interface Props {
   editable?: boolean;
   credit: Credit;
}

export const CreditInitial = ({ credit, editable }: Props) => {
  const refresh = useRefresh();
  const notify = useNotify();
  const [isReportOpened, setReportOpened] = useState(false);
  const [isTermsOpened, setTermsOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const canReportOpen = rbac.hasPermissionOrSuperadmin('credit-risk|ALL');

  const openReport = credit.risk ? () => setReportOpened(true) : undefined;
  const showContracts = (credit.user.contracts || []).length > 0;
  const score = credit.riskScore || 0;

  const handleStatusChange = async (newStatus: CreditStatus) => {
    if (loading) return;
    try {
      setLoading(true);
      await creditHttp.updateStatusProgress(credit.id, newStatus);
      notify('Credit Status is updated');
      refresh();
    } catch (err: any) {
      showAxiosError(err);
    } finally {
      setLoading(false);
    }
  };

  const hasAreaSales = rbac.hasAreaSales();
  const activeNegotiationButton = hasAreaSales && credit.status === CreditStatus.APPROVED;
  let negotiationButtonTitle = '';

  if (!activeNegotiationButton && !hasAreaSales) {
    negotiationButtonTitle = 'Only Sales Team can start negotiations';
  }
  if (!activeNegotiationButton && credit.status !== CreditStatus.APPROVED) {
    negotiationButtonTitle = 'Negotiations can be started only from Approved status';
  }

  return (
    <InitialPage title="Credit" backTo="/credit" backText="Back to all credits">
      <Row gutter={[20, 20]} className={cls.infoline}>
        <Col xs={24} lg={12} xl={8} className={cls.infolineCard}>
          <CreditCard credit={credit} />
        </Col>
        <Col xs={24} lg={12} xl={8} className={cls.infolineCard}>
          <InitialCard
            left="Risk manager"
            right={<CreditChatBtn credit={credit} />}
            wrapperClass={cls.risk}
          >
            <ScoreWidget
              value={score}
              percents={score > (credit?.risk?.successPercent || 0) ? 90 : 10}
              title="Risk manager"
              className={cls.score}
              onClick={canReportOpen ? openReport : undefined}
            >
              <div className={cls.scoreFooter}>
                <Button
                  color="secondary"
                  variant="contained"
                  className={cls.scoreBtn}
                  onClick={() => setTermsOpened(true)}
                  disabled={!credit.risk}
                >
                  Credit Terms
                </Button>

                {negotiationButtonTitle ? (
                  <LightTooltip title={negotiationButtonTitle}>
                    <div>
                      <Button
                        color="primary"
                        variant="contained"
                        className={cls.startNegotiationBtn}
                        onClick={activeNegotiationButton
                          ? () => handleStatusChange(CreditStatus.IN_NEGOTIATIONS)
                          : undefined}
                        disabled={!activeNegotiationButton}
                      >
                        Start Negotiation
                      </Button>
                    </div>
                  </LightTooltip>
                ) : (
                  <Button
                    color="primary"
                    variant="contained"
                    className={cls.startNegotiationBtn}
                    onClick={activeNegotiationButton
                      ? () => handleStatusChange(CreditStatus.IN_NEGOTIATIONS)
                      : undefined}
                    disabled={!activeNegotiationButton}
                  >
                    Start Negotiation
                  </Button>
                )}
              </div>
            </ScoreWidget>
          </InitialCard>
          <ExportCreditTerms
            creditId={credit.id}
            isOpened={isTermsOpened}
            setOpened={setTermsOpened}
          />
          <ExportRisk
            creditId={credit.id}
            isOpened={isReportOpened}
            setOpened={setReportOpened}
          />
        </Col>
        <Col xs={24} lg={12} xl={8} className={cls.infolineCard}>
          <CreditContractsDocuments credit={credit} editable={editable} />
        </Col>
        <Col xs={24} lg={12} xl={8} className={cls.infolineCard}>
          <CreditInvoiceWidget credit={credit} />
        </Col>
        <Col xs={24} lg={12} xl={8} className={cls.infolineCard}>
          <CreditCallsWidget
            credit={credit}
            wrapClass={cls.taskManagerWidget}
            contentClass={cls.taskManagerContent}
            title="Task Manager - by Credit"
          />
        </Col>
        <Col xs={24} lg={12} xl={8} className={cls.infolineCard}>
          <CreditTimeTracker credit={credit} />
        </Col>
      </Row>
      {showContracts && (
        <Row gutter={[20, 20]} className={cls.infoline}>
          <Col xs={24} lg={12} xl={8} className={cls.infolineCard}>
            <CreditContracts credit={credit} />
          </Col>
        </Row>
      )}

      <Row className={cls.infoline}>
        <Col xs={24} className={cls.infolineCard}>
          <CreditOrders credit={credit} />
        </Col>
      </Row>
    </InitialPage>
  );
};
